import { apiGet, apiPost } from "@/lib/api";
import type { QueryClient } from "@tanstack/react-query";
import type {
  CreateIncomingWebhookBody,
  IncomingWebhookDetailResponse,
  IncomingWebhookHistoryResponse,
  IncomingWebhookListResponse,
  IncomingWebhookRevokeResponse,
  IncomingWebhookSecretResponse,
} from "./types";

function collectionPath(workspaceId: string): string {
  return `/v1/workspaces/${encodeURIComponent(workspaceId)}/incoming-webhooks`;
}

function itemPath(workspaceId: string, hookId: string): string {
  return `${collectionPath(workspaceId)}/${encodeURIComponent(hookId)}`;
}

export function incomingWebhookListKey(userId: string, tenantId: string) {
  return ["incoming-webhooks", userId, tenantId] as const;
}

export function incomingWebhookRoomsKey(userId: string, tenantId: string) {
  return ["incoming-webhook-rooms", userId, tenantId] as const;
}

export function incomingWebhookDeliveriesKey(userId: string, tenantId: string, hookId: string) {
  return ["incoming-webhook-deliveries", userId, tenantId, hookId] as const;
}

export function discardIncomingWebhookQueries(
  queryClient: QueryClient,
  scope: { userId: string; tenantId: string },
): void {
  queryClient.removeQueries({ queryKey: incomingWebhookListKey(scope.userId, scope.tenantId) });
  queryClient.removeQueries({ queryKey: incomingWebhookRoomsKey(scope.userId, scope.tenantId) });
  queryClient.removeQueries({ queryKey: ["incoming-webhook-deliveries", scope.userId, scope.tenantId] });
}

export function listIncomingWebhooks(workspaceId: string) {
  return apiGet<IncomingWebhookListResponse>(collectionPath(workspaceId));
}

export function getIncomingWebhook(workspaceId: string, hookId: string) {
  return apiGet<IncomingWebhookDetailResponse>(itemPath(workspaceId, hookId));
}

export function createIncomingWebhook(workspaceId: string, body: CreateIncomingWebhookBody) {
  return apiPost<IncomingWebhookSecretResponse>(collectionPath(workspaceId), body);
}

export function rotateIncomingWebhook(workspaceId: string, hookId: string) {
  return apiPost<IncomingWebhookSecretResponse>(`${itemPath(workspaceId, hookId)}/rotate`, {});
}

export function revokeIncomingWebhook(workspaceId: string, hookId: string) {
  return apiPost<IncomingWebhookRevokeResponse>(`${itemPath(workspaceId, hookId)}/revoke`, {});
}

export function listIncomingWebhookDeliveries(workspaceId: string, hookId: string) {
  return apiGet<IncomingWebhookHistoryResponse>(`${itemPath(workspaceId, hookId)}/deliveries`);
}
